import IndexedDBServiceBase from "./indexedDB-service";
import DatabaseConfig from "./indexeddb-config.json";
import metaStoreService from "../metaStoreService/meta-store-service";
import * as Models from "../../../app/components/appwizard/component-wizards/wizards/source/database-reader/models";
import stringUtils from "../../utils/string-utils";
import dateLib from "../../utils/date-utils";

type wizardKey = "SOURCE" | "TARGET";

interface wizardIndexedDBDataTypes {
    SOURCE: Record<string, any>;
    TARGET: Record<string, any>;
    id: string;
    step: string;
    date: string;
}

interface WizardIndexedDBTypes {
    setSourceData(appName: string, data: Record<string, any>, step: string | null): Promise<boolean>;
    setTargetData(appName: string, data: Record<string, any>, step: string | null): Promise<boolean>;
    updateSourceModel(model: Record<string, any>, entireData: Record<string, any>): void;
    getAllDraftWizards(): Promise<wizardIndexedDBDataTypes[]>;
}

class WizardIndexedDBService extends IndexedDBServiceBase implements WizardIndexedDBTypes {
    constructor() {
        super(DatabaseConfig.WIZARD);
    }

    private async setWizardData(appName: string, data: Record<string, any>, key: wizardKey, step: string | null) {
        if (!appName || !data || !key) return;
        try {
            const existingData = await this.getDataById(appName);
            const currentTime = dateLib();
            const updatedData = {
                ...existingData,
                [key]: JSON.parse(JSON.stringify(data)),
                id: appName,
                step: step || window.location.hash,
                date: currentTime?.valueOf()
            };
            return await this.putData(updatedData);
        } catch (e) {
            console.error("Error when trying to put wizard data:", e);
        }
    }

    private prepareSchemasCollection(schemas: Record<string, any>[]) {
        let schemasCollection = new Models.Schemas();
        schemas.forEach(schema => {
            schemasCollection.add(
                new Models.Schema().set({
                    id: schema.id,
                    isEnabled: schema.isEnabled,
                    isDisabled: schema.isDisabled,
                    schemaName: schema.schemaName,
                    schemaDisplayName: stringUtils.encodeHtml(schema.schemaDisplayName),
                    tables: this.prepareTablesCollection(schema.tables),
                    errors : schema.errors
                })
            );
        });
        return schemasCollection;
    }

    private prepareTablesCollection(tables: Record<string, any>[]) {
        let tableCollection = new Models.Tables();
        tables.forEach(table => {
            tableCollection.add(new Models.Table(table));
        });
        return tableCollection;
    }

    public async setSourceData(appName, data, step = null) {
        if (!appName || !data) return;
        return await this.setWizardData(appName, data, metaStoreService.entities.SOURCE, step);
    }

    public async setTargetData(appName, data, step = null) {
        if (!appName || !data) return;
        return await this.setWizardData(appName, data, metaStoreService.entities.TARGET, step);
    }

    public async updateSourceModel(model, entireData) {
        const sourceData = entireData[metaStoreService.entities.SOURCE];
        if (sourceData) {
            model.set(sourceData);
            if (sourceData.connectionParams) {
                const updatedConnectionParams = new Models.ConnectionParams(sourceData.connectionParams);
                model.set("connectionParams", updatedConnectionParams);
            }
            if (sourceData.schemas) {
                const updatedSchemas = this.prepareSchemasCollection(sourceData.schemas);
                model.set("schemas", updatedSchemas);
            }
            if (sourceData.channels) {
                const updatedSchemas = this.prepareSchemasCollection(sourceData.channels);
                model.set("channels", updatedSchemas);
            }
        } else {
            console.error("Source data not present in the prefill values");
        }
    }

    public async getAllDraftWizards() {
        const draftWizards = await this.getAllData();
        return draftWizards as wizardIndexedDBDataTypes[];
    }
}

export const wizardIndexedDBService = new WizardIndexedDBService();
